import usePanggilanAntrian from '../hooks/usePanggilanAntrian';
import { labelAntrian } from '../utils/antrian';
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Divider, 
  Chip, 
  LinearProgress, 
  Stack, 
  Card, 
  CardContent, 
  List, 
  ListItem, 
  ListItemText,
  Button
} from '@mui/material';
import { styled } from '@mui/system';
import { format } from 'date-fns';
import Swal from 'sweetalert2';
import { 
  CheckCircle, 
  ClockCounterClockwise, 
  CookingPot,
  Receipt,
  Truck,
  CheckSquare
} from '@phosphor-icons/react';
import { apiHost } from '../utils/apiHost';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  const baseUrl = (apiHost() || '').replace(/^https?:\/\//, '');
  return baseUrl ? `${protocol}://${baseUrl}` : `${window.location.origin.replace(/\/$/, '')}`;
};

const StatusChip = styled(Chip)(({ theme, status }) => ({
  fontWeight: 'bold',
  ...(status === 'paid' && {
    backgroundColor: '#e6f7ed',
    color: '#00a854',
  }),
  ...(status === 'accepted' && {
    backgroundColor: '#e6f7ff',
    color: '#0077cc',
  }),
  ...(status === 'processing' && {
    backgroundColor: '#fff7e6',
    color: '#fa8c16',
  }),
  ...(status === 'ready' && {
    backgroundColor: '#f0f5ff',
    color: '#1890ff',
  }),
  ...(status === 'delivering' && {
    backgroundColor: '#fff0f6',
    color: '#eb2f96',
  }),
  ...(status === 'completed' && {
    backgroundColor: '#e6fffb',
    color: '#13c2c2',
  }),
  ...(status === 'waiting' && {
    backgroundColor: '#f5f5f5',
    color: '#595959',
  }),
}));

const StatusIcon = ({ status }) => {
  switch (status) {
    case 'paid':
      return <CheckCircle size={24} weight="fill" color="#00a854" />;
    case 'accepted':
      return <Receipt size={24} weight="fill" color="#0077cc" />;
    case 'processing':
      return <CookingPot size={24} weight="fill" color="#fa8c16" />;
    case 'ready':
      return <CheckSquare size={24} weight="fill" color="#1890ff" />;
    case 'delivering':
      return <Truck size={24} weight="fill" color="#eb2f96" />;
    case 'completed':
      return <CheckCircle size={24} weight="fill" color="#13c2c2" />;
    case 'waiting':
      return <ClockCounterClockwise size={24} weight="fill" color="#595959" />;
    default:
      return null;
  }
};

const StepBox = styled(Box)(({ theme, active }) => ({
  padding: '16px',
  borderRadius: '8px',
  backgroundColor: active ? '#f0f5ff' : '#f5f5f5',
  borderLeft: active ? '4px solid #1890ff' : '4px solid transparent',
  transition: 'all 0.3s ease',
}));

const OrderStatusPage = () => {
  const location = useLocation();
  const params = useParams();
  const [orderId, setOrderId] = useState(null);
  const [orderData, setOrderData] = useState(null);
  usePanggilanAntrian(orderData?.orderDetails?.jumlahPanggil); // getar + bunyi saat kasir memanggil nomor
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const pollingRef = useRef(null);

  useEffect(() => {
    const orderIdFromParams = params.orderId;
    const searchParams = new URLSearchParams(location.search);
    const orderIdFromQuery = searchParams.get('order_id');
    
    const orderIdToUse = orderIdFromParams || orderIdFromQuery;
    
    if (orderIdToUse) {
      setOrderId(orderIdToUse);
      setError(null);
      fetchOrderStatus(orderIdToUse);
      // Auto-refresh setiap 15 detik agar customer bisa memantau update dari dapur
      pollingRef.current = setInterval(() => {
        fetchOrderStatus(orderIdToUse, true);
      }, 15000);
    } else {
      setError("Order ID tidak ditemukan. Periksa URL Anda.");
      setLoading(false);
    }
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [location, params]);

  const fetchOrderStatus = async (id, isPolling = false) => {
    try {
      if (!isPolling) setLoading(true);
      const encodedOrderId = encodeURIComponent(id);
      const baseUrl = getApiBaseUrl();
      const response = await axios.get(`${baseUrl}/status/${encodedOrderId}`);
      
      if (response.data && response.data.status === true && response.data.data) {
        setOrderData(response.data.data);
        setError(null);
      } else {
        if (!isPolling) setError(response.data?.message || 'Data pesanan tidak valid.');
      }
    } catch (err) {
      console.error("API Error:", err);
      const msg = err.response?.data?.message || err.message || 'Gagal memuat status pesanan.';
      setError(msg);
      if (!orderData && !isPolling) {
        Swal.fire({
          title: 'Error',
          text: msg,
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    } finally {
      if (!isPolling) setLoading(false);
    }
  };

  const refreshStatus = () => {
    if (orderId) {
      fetchOrderStatus(orderId, false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h5" align="center" gutterBottom>
            Memuat Status Pesanan...
          </Typography>
          <LinearProgress sx={{ mt: 2 }} />
        </Paper>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h5" align="center" color="error" gutterBottom>
            Error
          </Typography>
          <Typography align="center">{error}</Typography>
          <Box display="flex" justifyContent="center" mt={2}>
            <Button variant="contained" color="primary" onClick={() => window.location.href = '/'}>
              Kembali ke Beranda
            </Button>
          </Box>
          <Box display="flex" justifyContent="center" mt={2}>
            <Button variant="outlined" color="primary" onClick={refreshStatus}>
              Coba Lagi
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  if (!orderData) {
    return null;
  }

  const { orderDetails, statusInfo, statusMessage, estimatedTime, kitchenStatus } = orderData;

  const statusUrutan = ['baru', 'diproses', 'siapdiantar', 'diantar', 'selesai'];
  const currentStatusIndex = statusUrutan.indexOf((kitchenStatus || 'baru').toLowerCase());
  const isStagePassed = (stage) => currentStatusIndex > statusUrutan.indexOf(stage);

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, borderRadius: 2 }}>
        <Box display="flex" justifyContent="space-between" flexWrap="wrap" mb={3}>
          <Typography variant="h4" component="h1" gutterBottom>
            Status Pesanan
          </Typography>
          <Button 
            variant="outlined" 
            onClick={refreshStatus} 
            startIcon={<ClockCounterClockwise weight="bold" />}
          >
            Refresh
          </Button>
        </Box>

        <Card sx={{ mb: 4, backgroundColor: '#f9f9f9', borderRadius: 2 }}>
          <CardContent>
            {labelAntrian(orderDetails) && (
              <Box sx={{ textAlign: 'center', mb: 2, py: 1.5, bgcolor: '#fff8e1', border: '1px dashed #f9a825', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary">Nomor Antrian</Typography>
                <Typography sx={{ fontSize: 40, fontWeight: 900, lineHeight: 1.1, color: '#e65100' }}>{labelAntrian(orderDetails)}</Typography>
                {orderDetails.dipanggilAt && (
                  <Typography sx={{ mt: 0.5, fontWeight: 700, color: '#2e7d32' }}>Pesanan Anda dipanggil, silakan ke kasir!</Typography>
                )}
              </Box>
            )}
            <Stack direction="row" justifyContent="space-between" flexWrap="wrap" spacing={2}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Order ID
                </Typography>
                <Typography variant="subtitle1" fontWeight="bold">
                  {orderDetails.order_id}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Tanggal
                </Typography>
                <Typography variant="subtitle1">
                  {format(new Date(orderDetails.tanggal), 'dd MMMM yyyy')}
                </Typography>
              </Box>
              {orderDetails.Table && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Meja
                  </Typography>
                  <Typography variant="subtitle1">
                    {orderDetails.Table.name}
                  </Typography>
                </Box>
              )}
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Pembayaran
                </Typography>
                <Typography variant="subtitle1" textTransform="uppercase">
                  {orderDetails.pembayaran}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
          Status Pemrosesan
        </Typography>

        <Box mb={4}>
          <StepBox active={statusInfo.paid} mb={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <StatusIcon status="paid" />
              <Box flex={1}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Pembayaran
                </Typography>
                <Typography variant="body2">
                  {statusInfo.paid ? 'Pembayaran berhasil' : 'Menunggu pembayaran'}
                </Typography>
              </Box>
              <StatusChip 
                label={statusInfo.paid ? "Dibayar" : "Menunggu"} 
                status="paid" 
                variant={statusInfo.paid ? "filled" : "outlined"}
              />
            </Stack>
          </StepBox>

          <StepBox active={statusInfo.accepted} mb={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <StatusIcon status="accepted" />
              <Box flex={1}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Pesanan Diterima
                </Typography>
                <Typography variant="body2">
                  {statusInfo.accepted ? 'Pesanan sudah diterima oleh kasir' : 'Menunggu konfirmasi kasir'}
                </Typography>
              </Box>
              <StatusChip 
                label={statusInfo.accepted ? "Diterima" : "Menunggu"} 
                status="accepted" 
                variant={statusInfo.accepted ? "filled" : "outlined"}
              />
            </Stack>
          </StepBox>

          <StepBox active={statusInfo.processing} mb={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <StatusIcon status="processing" />
              <Box flex={1}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Pemrosesan di Dapur
                </Typography>
                <Typography variant="body2">
                  {isStagePassed('diproses') ? 'Pesanan sudah selesai diproses' : statusInfo.processing ? 'Pesanan sedang diproses di dapur' : 'Menunggu pemrosesan'}
                </Typography>
                {statusInfo.processing && !isStagePassed('diproses') && estimatedTime && (
                  <Typography variant="body2" color="primary" fontWeight="medium">
                    Estimasi waktu: {estimatedTime}
                  </Typography>
                )}
              </Box>
              <StatusChip
                label={isStagePassed('diproses') ? "Selesai" : statusInfo.processing ? "Diproses" : "Menunggu"}
                status="processing"
                variant={statusInfo.processing ? "filled" : "outlined"}
              />
            </Stack>
          </StepBox>

          <StepBox active={statusInfo.ready} mb={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <StatusIcon status="ready" />
              <Box flex={1}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Siap Diantar
                </Typography>
                <Typography variant="body2">
                  {isStagePassed('siapdiantar') ? 'Pesanan sudah diantar' : statusInfo.ready ? 'Pesanan siap untuk diantar' : 'Menunggu persiapan'}
                </Typography>
                {statusInfo.ready && !isStagePassed('siapdiantar') && estimatedTime && (
                  <Typography variant="body2" color="primary" fontWeight="medium">
                    Estimasi waktu: {estimatedTime}
                  </Typography>
                )}
              </Box>
              <StatusChip
                label={isStagePassed('siapdiantar') ? "Selesai" : statusInfo.ready ? "Siap" : "Menunggu"}
                status="ready"
                variant={statusInfo.ready ? "filled" : "outlined"}
              />
            </Stack>
          </StepBox>

          <StepBox active={statusInfo.delivering} mb={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <StatusIcon status="delivering" />
              <Box flex={1}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Pengantaran
                </Typography>
                <Typography variant="body2">
                  {isStagePassed('diantar') ? 'Pesanan sudah sampai' : statusInfo.delivering ? 'Pesanan sedang diantar ke meja Anda' : 'Belum diantar'}
                </Typography>
                {statusInfo.delivering && !isStagePassed('diantar') && estimatedTime && (
                  <Typography variant="body2" color="primary" fontWeight="medium">
                    Estimasi waktu: {estimatedTime}
                  </Typography>
                )}
              </Box>
              <StatusChip
                label={isStagePassed('diantar') ? "Selesai" : statusInfo.delivering ? "Diantar" : "Menunggu"}
                status="delivering"
                variant={statusInfo.delivering ? "filled" : "outlined"}
              />
            </Stack>
          </StepBox>

          <StepBox active={statusInfo.completed} mb={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <StatusIcon status="completed" />
              <Box flex={1}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Selesai
                </Typography>
                <Typography variant="body2">
                  {statusInfo.completed ? 'Pesanan telah selesai' : 'Menunggu penyelesaian'}
                </Typography>
              </Box>
              <StatusChip 
                label={statusInfo.completed ? "Selesai" : "Menunggu"} 
                status="completed" 
                variant={statusInfo.completed ? "filled" : "outlined"}
              />
            </Stack>
          </StepBox>
        </Box>

        <Box mt={3} mb={4} p={2} bgcolor="#f5f5f5" borderRadius={2}>
          <Typography variant="h6" gutterBottom>
            Status Pesanan:
          </Typography>
          <Typography variant="body1" fontWeight="medium" color="primary">
            {statusMessage}
          </Typography>
          {estimatedTime && estimatedTime !== 'Selesai' && (
            <Typography variant="body2" color="text.secondary" mt={1}>
              Estimasi waktu: {estimatedTime}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h5" gutterBottom>
          Detail Pesanan
        </Typography>

        <List>
          {orderDetails.TransaksiDetails.map((item, index) => (
            <ListItem 
              key={index} 
              divider={index < orderDetails.TransaksiDetails.length - 1}
              sx={{ px: 0 }}
            >
              <ListItemText
                primary={
                  <Typography variant="subtitle1" fontWeight="medium">
                    {item.Barang.namabarang}
                  </Typography>
                }
                secondary={
                  <>
                    <Typography variant="body2" color="text.secondary">
                      {item.Barang.Kategori.namakategori}
                    </Typography>
                    {item.catatan && (
                      <Typography variant="body2" fontStyle="italic" color="warning.main">
                        Catatan: {item.catatan}
                      </Typography>
                    )}
                  </>
                }
              />
              <Stack direction="column" alignItems="flex-end">
                <Typography variant="body2" color="text.secondary">
                  {item.jumlahbarang} x Rp {parseFloat(item.harga).toLocaleString('id-ID')}
                </Typography>
                <Typography variant="subtitle1" fontWeight="bold">
                  Rp {parseFloat(item.total).toLocaleString('id-ID')}
                </Typography>
              </Stack>
            </ListItem>
          ))}
        </List>

        <Box mt={3} p={2} bgcolor="#f9f9f9" borderRadius={2}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="h6">Total</Typography>
            <Typography variant="h6" fontWeight="bold">
              Rp {parseFloat(orderDetails.totaljual).toLocaleString('id-ID')}
            </Typography>
          </Stack>
        </Box>
      </Paper>
    </Container>
  );
};

export default OrderStatusPage;